import React from "react";
import Modal from "react-modal";
import { ProductDetailInfo } from "../ProductDetailPageTypeConfig";
import currencyFormatter, { Currency } from "../../../../js/common/CurrencyFormatter";
import { SellTag } from "../../../../js/models/maxEnums";
import sellTagService from "../../../../js/common/SellTagService";
import detailTabSwitchService from "../DetailTabSwitchService";
import ProductErrorReportModal from "./ProductErrorReportModal";

interface ProductPriceComparisonModalProperties {
    readonly productDetailInfo: ProductDetailInfo;
    readonly cheapestPrice: number;
    readonly newProductsDiscountCode: string;
    readonly actionDiscountCode: string | null;
    readonly megaClearanceSaleDiscountCode: string | null;
    readonly currency: Currency;

    readonly onAfterClose?: () => void;
    readonly onAddToBasket?: (productId: number) => void;
}

const discountBlock = (productDetailInfo: ProductDetailInfo, actionDiscountCode: string | null, megaClearanceSaleDiscountCode: string | null): React.JSX.Element | undefined => {
    if (productDetailInfo.discountedPrice !== null && actionDiscountCode !== null && sellTagService.contains(productDetailInfo.sellTags, SellTag.Promo)) {
        return (
            <p>
                <i className="fa-regular fa-clock"></i>&nbsp;<strong>Pouze do dnešní půlnoci</strong> se slevovým kódem <strong>{actionDiscountCode}</strong>
            </p>
        );
    }

    if (productDetailInfo.discountedPrice !== null && megaClearanceSaleDiscountCode !== null && sellTagService.contains(productDetailInfo.sellTags, SellTag.MegaClearanceSale)) {
        return (
            <p>
                <i className="fa-regular fa-clock"></i>&nbsp;<strong>Pouze do dnešní půlnoci</strong> se slevovým kódem <strong>{megaClearanceSaleDiscountCode}</strong>
            </p>
        );
    }

    return undefined;
};

export default function ProductPriceComparisonModal({
    productDetailInfo,
    cheapestPrice,
    newProductsDiscountCode,
    actionDiscountCode,
    megaClearanceSaleDiscountCode,
    currency,
    onAfterClose,
    onAddToBasket,
}: ProductPriceComparisonModalProperties): React.JSX.Element | undefined {
    const [modalIsOpen, setModalIsOpen] = React.useState(true);

    const closeModal = (): void => {
        setModalIsOpen(false);
    };

    const handleGoToBuy = (): void => {
        if (onAddToBasket !== undefined) {
            onAddToBasket(productDetailInfo.productId);

            return;
        }

        detailTabSwitchService.switchAndScrollToVariantsTab();
        closeModal();
    };

    if (productDetailInfo.retailPrice === null || productDetailInfo.asin === null) {
        closeModal();

        return undefined;
    }

    return (
        <Modal
            isOpen={modalIsOpen}
            onRequestClose={closeModal}
            onAfterClose={onAfterClose}
            className="modal-dialog modal-transition"
            overlayClassName="react-modal-backdrop modal-transition"
        >
            <div className="modal-content">
                <div className="modal-header empty modal-header-image">
                    <button type="button" className="close" onClick={closeModal}>
                        <i className="fa-solid fa-times"></i>
                    </button>
                </div>

                <div className="modal-body">
                    <div className="well well-transparent">
                        <div className="product-row">
                            <p className="fakeh3">Se mnou ušetříte až {Math.round((1 - (cheapestPrice / productDetailInfo.retailPrice)) * 100)} %</p>
                            <p className="original-price-claim first">Nové zboží stojí běžně na
                                internetu {currencyFormatter.formatCurrencyWithSymbol(productDetailInfo.retailPrice, currency)}. Přesvědčte se na vlastní oči &#x1F609;
                            </p>
                            <p className="original-price-claim"><a
                                href={`https://www.amazon.de/dp/${encodeURIComponent(productDetailInfo.asin)}?language=cs_CZ&th=1&psc=1`} target="_blank"
                                rel="noreferrer">Zkontrolovat cenu zboží na Amazonu
                            </a>
                            </p>
                            <p className="original-price-claim">Zakoupením tak <strong>ušetříte až
                                &nbsp;
                            {Math.round((1 - (cheapestPrice / productDetailInfo.retailPrice)) * 100)} %
                                (<span
                                className="green">až {currencyFormatter.formatCurrencyWithSymbol(productDetailInfo.retailPrice - (productDetailInfo.discountedPrice ?? cheapestPrice), currency)}
                            </span>)
                            </strong>.
                            </p>
                            <div className="actual-price">
                                <p>
                                    {productDetailInfo.discountedPrice !== null && productDetailInfo.sellTags === SellTag.IsNew
                                        ? <span><strong>Dnes AKČNÍ CENA</strong> s kódem
                                            {newProductsDiscountCode}
                                        </span>
                                        : <>Cena pro vás:</>}
                                </p>
                                <div className="price-now">od {currencyFormatter.formatCurrencyWithSymbol(cheapestPrice, currency)}</div>
                                <div className="price-discount-tag">-{Math.round((1 - (cheapestPrice / productDetailInfo.retailPrice)) * 100)} %</div>

                                {discountBlock(productDetailInfo, actionDiscountCode, megaClearanceSaleDiscountCode)}

                                <div className="discount-modal-buttons">
                                    <button type="button" className="btn btn-lg btn-cart product-page-buy-button detail-new-change3" onClick={handleGoToBuy}>Koupit</button>
                                    <button type="button" className="btn btn-grey" onClick={closeModal}>Zavřít</button>
                                </div>
                            </div>
                            <ProductErrorReportModal productDetailInfo={productDetailInfo} />
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
